import React, { useContext, useEffect, useState } from "react";
import {
  Grid, FormControl, InputLabel, Select, MenuItem, Button,
  Dialog, DialogTitle, Typography, SelectChangeEvent, ListSubheader
} from "@mui/material";

import MuiIcon from "../../../../../../../../shared/styles/mui/helpers/mui-icon.helper";
import IReportSelectedColumn from "../../../../../../../../shared/contracts/IReportSelectedColumn";
import { FILTER_OPERATOR } from "../../../../../../../../shared/components/filters/contracts/TFilterOperator";
import IReportFilterCriteria from "../../../../../../../../shared/contracts/IReportFilterCriteria";
import IReportAvailableColumn from "../../../../../../../../shared/contracts/IReportAvailableColumn";
import IFilterOperator from "../../../../../../../../shared/contracts/IFilterOperator";
import useHelpers from "../../../../../../../../shared/services/helpers/useHelpers.hook";
import { REPORT_COLUMN_DATA_TYPE } from "../../../../../../../../shared/contracts/TReportColumnDataType";
import IFilterCategory from "../../../../../../../../shared/contracts/IFilterCategory";
import useReportingAndAnalyticsApi from "../../../../../useReportingAndAnalyticsApi";
import { ConditionalRangeTextFields } from "../../../../../../../../shared/components/conditional-range-text-fields/conditional-range-text-fields.component";
import { AdvancedFilterOptionsContext } from "../../../../../../../../shared/context/advanced-filter-options/advanced-filter-options-context";
import ReportDataFiltersDialogService from "../../report-data-filters-dialog.service";


function FilterFieldDialogComponent({
  targetFilterIndex, targetFilterItem, defaultAvailableColumns, filterCategories,
  onCancel = () => { },
  onApply = (filterIndex: number, filterItem: IReportFilterCriteria) => { },
}: {
  targetFilterIndex: number;
  targetFilterItem: IReportFilterCriteria;
  defaultAvailableColumns: IReportAvailableColumn[];
  filterCategories: IFilterCategory[];
  onApply: (filterIndex: number, filterItem: IReportFilterCriteria) => void;
  onCancel: () => void;
}) {

  const { advancedFilterOptions } = useContext(AdvancedFilterOptionsContext)!;
  const { filterOperatorValues, stringFilters, numberFilters, dateFilters, filterOperators } = advancedFilterOptions;
  const { getFilterOperatorsList } = useReportingAndAnalyticsApi();

  const { getKeyByValue } = useHelpers();
  const [hasValidChanges, setHasValidChanges] = useState<boolean>(false);

  const [filterField, setFilterField] = useState<IReportSelectedColumn | null>();
  const [openFilterField, setOpenFilterField] = useState<boolean>(false);

  const [filterOperator, setFilterOperator] = useState<IFilterOperator | null>(null);
  const [openFilterOperator, setOpenFilterOperator] = useState<boolean>(false);

  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [openFilterValue, setOpenFilterValue] = useState<boolean>(false);

  /// Function to group options by category
  const groupOptionsByCategory = (options: IFilterOperator[]): Record<string, IFilterOperator[]> => {
    return options.reduce((groups, option) => {
      const category = option.categoryId;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(option);
      return groups;
    }, {} as Record<string, IFilterOperator[]>);
  };

  const groupedOptions = groupOptionsByCategory(filterOperators);

  // Apply filter to the groupedOptions
  const filteredGroupedOptions = filterField ?
    Object.keys(groupedOptions).reduce((filtered, category) => {
      const filterFieldType = getKeyByValue(REPORT_COLUMN_DATA_TYPE, filterField?.columnDataType ?? REPORT_COLUMN_DATA_TYPE.VARCHAR) ?? REPORT_COLUMN_DATA_TYPE.VARCHAR;//TODO test

      const filterCategories = getFilterCategoriesByDataType(filterFieldType);


      const filteredOptions = groupedOptions[category].filter((option) =>
        filterCategories.includes(option.categoryId)
      );
      if (filteredOptions.length > 0) {
        filtered[category] = filteredOptions;
      }
      return filtered;
    }, {} as Record<string, IFilterOperator[]>) : groupedOptions;

  const categoryName = (categoryId: number) => {
    return filterCategories.find(c => c.id == categoryId)?.name ?? categoryId;
  }

  function getFilterCategoriesByDataType(columnDataType: string) {
    const filterMapping: Record<string, any> = {
      [REPORT_COLUMN_DATA_TYPE.DATE]: dateFilters,
      [REPORT_COLUMN_DATA_TYPE.NUMBER]: numberFilters,
    };
    return filterMapping[columnDataType] ?? stringFilters;
  }

  useEffect(() => {
    if (targetFilterIndex === -1) {
      if (filterField) {
        setOpenFilterField(false);
        if (filterOperator) {
          setOpenFilterOperator(false);
          if (
            filterOperator.optionValue.toUpperCase() === FILTER_OPERATOR.BLANK ||
            filterOperator.optionValue.toUpperCase() === FILTER_OPERATOR.NOT_BLANK
          ) {
            setFilterValue(null);
            setHasValidChanges(true);
          }
          else {
            setHasValidChanges(true);
          }
        } else {
          setOpenFilterOperator(true);
          //setFilterOperator(FILTER_OPERATOR.EQUALS);
          setHasValidChanges(false);
          setFilterValue(null);
        }
      } else {
        setHasValidChanges(false);
        setFilterOperator(null);
        setFilterValue(null);
        setOpenFilterField(true);
      }
    } else {
      const hasChanges =
        filterOperator?.optionValue !== targetFilterItem.operator ||
        filterValue !== targetFilterItem.value;
      setHasValidChanges(hasChanges);
    }
  }, [
    targetFilterIndex,
    targetFilterItem,
    filterField,
    filterOperator,
    filterValue,
  ]);

  useEffect(() => {
    if (targetFilterIndex >= 0) {
      // field()
      const parentField = defaultAvailableColumns.find(
        (x) => x.columnName === targetFilterItem.column
      );
      setFilterField(parentField ? parentField : null);
      // operator
      const parentOperator = Object.values(filterOperators).find(
        (x) => x.optionValue === targetFilterItem.operator
      );
      setFilterOperator(parentOperator ? parentOperator : null);
      // value
      setFilterValue(targetFilterItem.value ? targetFilterItem.value : null);
    }
  }, [targetFilterIndex, targetFilterItem, defaultAvailableColumns]);

  const handleFilterFieldChange = (e: SelectChangeEvent<number>) => {
    const menuItemIndex = +e.target.value;
    if (menuItemIndex >= 0) {
      setFilterField(defaultAvailableColumns[menuItemIndex]);
    } else {
      setFilterField(null);
    }
  };

  const handleFilterOperatorChange = (e: SelectChangeEvent<string>) => {
    setFilterValue(null);
    const menuItemIndex = e.target.value;
    if (menuItemIndex) {
      setFilterOperator(filterOperators.find(opt => opt.optionValue == menuItemIndex) ?? null);
    } else {
      setFilterOperator(null);
    }
  };

  const handleFilterValueDdlChange = (e: SelectChangeEvent<string>) => {
    const menuItemIndex = e.target.value;
    if (menuItemIndex) {
      setFilterValue(filterOperatorValues.find(op => op.key == menuItemIndex)?.key ?? null);
    } else {
      setFilterValue(null);
    }
  };

  const handleOnApply = () => {
    if (filterField && filterOperator) {
      onApply(targetFilterIndex, {
        column: filterField.columnName,
        columnDescription: filterField.columnDescription,
        description: filterField.columnDescription,
        operator: filterOperator.optionValue,
        value: filterValue,
        operatorName: filterOperator.optionName,
        operatorValueId: filterOperator.operatorValueId,
      });
    }
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={targetFilterItem !== null}
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Grid container direction="row" wrap="nowrap">
            <Grid
              item
              xs={6}
              alignItems="center"
              container
              direction="row"
              wrap="nowrap"
            >
              <Grid item container direction="column" wrap="nowrap">
                <Typography flexGrow={1} variant="h6">
                  {targetFilterIndex === -1 ? "Add New" : "Update"} Filter
                </Typography>
              </Grid>
            </Grid>

            <Grid
              item
              xs={6}
              container
              direction="row"
              justifyContent="end"
              alignItems="center"
            >
              <Button
                color="error"
                size="small"
                variant="text"
                onClick={onCancel}
                sx={{ borderRadius: "24px", mr: 2 }}
              >
                Cancel
              </Button>

              <Button
                color="info"
                size="small"
                variant="contained"
                disabled={!hasValidChanges}
                sx={{ borderRadius: "24px", pr: 2 }}
                onClick={handleOnApply}
                startIcon={MuiIcon.ADD_CIRCLE()}
              >
                {targetFilterIndex === -1 ? "Add New" : "Update"}
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>

        <Grid
          container
          direction="column"
          justifyItems="stretch"
          justifyContent="stretch"
          sx={{
            px: 2,
            py: 2,
            width: "100%",
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          {/* Filter-Field */}
          <Grid item sx={{ pr: 2 }}>
            <FormControl
              disabled={targetFilterIndex >= 0}
              focused={openFilterField}
              fullWidth
              size="small"
              required
              sx={{ m: 1, minWidth: 120 }}
            >
              <InputLabel>Filter Field</InputLabel>

              <Select
                open={openFilterField}
                onOpen={() => setOpenFilterField(true)}
                onClose={() => setOpenFilterField(false)}
                value={
                  filterField
                    ? defaultAvailableColumns.findIndex(
                      (x) =>
                        x.columnId === filterField.columnId &&
                        x.columnName === filterField.columnName
                    )
                    : -1
                }
                label="Filter Field *"
                onChange={handleFilterFieldChange}
              >
                <MenuItem value={-1}>
                  <em>None</em>
                </MenuItem>

                {defaultAvailableColumns.map((col, index) => {
                  return (
                    <MenuItem key={`filterMenuItem-${index}`} value={index}>
                      {col.columnDescription}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Filter-Criteria */}
          {filterField && (
            <Grid item sx={{ pr: 2 }}>
              <FormControl
                focused={openFilterOperator}
                fullWidth
                size="small"
                required
                sx={{ m: 1, minWidth: 120 }}
              >
                <InputLabel>Filter Operator</InputLabel>

                <Select
                  open={openFilterOperator}
                  onOpen={() => setOpenFilterOperator(true)}
                  onClose={() => setOpenFilterOperator(false)}
                  value={filterOperator?.optionValue ?? ""}
                  label="Filter Operator *"
                  onChange={handleFilterOperatorChange}
                >
                  <MenuItem value={-1}>
                    <em>None</em>
                  </MenuItem>

                  {Object.keys(filteredGroupedOptions).map((category) => [
                    <ListSubheader>{categoryName(category as any)}</ListSubheader>,
                    ...filteredGroupedOptions[category].map((option) => (
                      <MenuItem key={option.optionId} value={option.optionValue}>
                        {option.optionName}
                      </MenuItem>
                    )),
                  ])}

                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Filter-Value */}
          {filterOperator &&
            filterOperator.optionValue.toUpperCase() !== FILTER_OPERATOR.BLANK &&
            filterOperator.optionValue.toUpperCase() !== FILTER_OPERATOR.NOT_BLANK &&
            (
              <Grid item sx={{ pr: 2 }}>
                <FormControl
                  focused={openFilterValue}
                  fullWidth
                  size="small"
                  sx={{ m: 1, minWidth: 120 }}
                >
                  {!ReportDataFiltersDialogService.isOperatorValueFieldDdl(filterOperator.operatorValueId) &&
                    <ConditionalRangeTextFields
                      showRangeTextFields={ReportDataFiltersDialogService.isRangeFilterOperatorValue(filterOperator.optionValue)}
                      filterValue={filterValue}
                      setFilterValue={setFilterValue}
                    />
                  }

                  {ReportDataFiltersDialogService.isOperatorValueFieldDdl(filterOperator.operatorValueId) &&
                    <>
                      <InputLabel shrink={true}>Value</InputLabel>
                      <Select
                        open={openFilterValue}
                        onOpen={() => setOpenFilterValue(true)}
                        onClose={() => setOpenFilterValue(false)}
                        value={filterValue ?? ""}
                        renderValue={(value) => {
                          const displayValue = filterOperatorValues.find((x) => x.key === filterValue)?.value;
                          return displayValue || '';
                        }}
                        displayEmpty
                        label="Value"
                        onChange={handleFilterValueDdlChange}
                      >
                        <MenuItem value={-1}>
                          <em>None</em>
                        </MenuItem>
                        {filterOperatorValues.map((col, index) => {
                          return (
                            <MenuItem key={`filterMenuItem-${index}`} value={col.key}>
                              {col.value}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </>
                  }
                </FormControl>
              </Grid>
            )}
        </Grid>
      </Dialog>

    </>
  );
}
export default FilterFieldDialogComponent;
